import { BusinessOutlined, PersonOutline, ShieldOutlined } from "@mui/icons-material";
import { Box, Stack } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, MyModal, SelectInput, SiteSelect, TextInput } from "components";
import { ENDPOINTS, USER_TYPES } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserType } from "types";
import { validateName } from "utils";

const defaultValue = {
  name: "",
  email: ""
};
const defaultValues = {
  ...defaultValue,
  type: USER_TYPES[0] as UserType,
  siteId: "" as string
};
const initState =
  process.env.NODE_ENV === "development"
    ? ({
        ...defaultValues,
        name: "Test User",
        email: "test@user.com"
      } as typeof defaultValues)
    : defaultValues;

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

interface IAddUser {
  onSuccess?: () => void;
  onClose: () => void;
}

export default function AddUser({ onSuccess, onClose }: IAddUser) {
  const [state, setState] = useState(initState);
  const [errors, setErrors] = useState(defaultValue);

  const t = useTranslation();
  const navigate = useNavigate();
  const needsSite = state.type !== "super";
  const disabledSubmit = useMemo(
    () => !!(errors.name || !state.name || errors.email || !state.email || (needsSite && !state.siteId)),
    [state, errors, needsSite]
  );
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch<{ _id: string }>("POST");
  const { enqueueSnackbar } = useSnackbar();
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      const body: Record<string, string> = {
        name: state.name,
        email: state.email,
        type: state.type
      };
      if (state.type !== "super") {
        body.siteId = state.siteId;
      }
      callFetchPost(ENDPOINTS.users, {
        body: JSON.stringify(body)
      }).catch(console.error);
    },
    [state, callFetchPost]
  );
  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
      setState(initState);
    }
  }, [response, onClose, enqueueSnackbar, onSuccess, navigate]);
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal title={t["Add User"]} open={true} setOpen={onClose} variant="light">
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            value={state.name}
            label={t["Name"]}
            type="text"
            placeholder={t["e.g John Doe"]}
            error={!!errors.name}
            helperText={errors.name}
            startAdornment={<PersonOutline sx={iconSx} />}
            sx={fieldSx}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                name: e.target.value
              }));
              setErrors((prevState) => ({
                ...prevState,
                name: validateName(e.target.value)
              }));
            }}
            fullWidth
            required
            autoFocus
          />
          <TextInput
            value={state.email}
            label={t["Email"]}
            type="email"
            placeholder={t["e.g johndoes@gmail.com"]}
            error={!!errors.email}
            helperText={errors.email}
            sx={fieldSx}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                email: e.target.value
              }));
              setErrors((prevState) => ({
                ...prevState,
                email: !!e.target.value ? "" : t["Email Address required"]
              }));
            }}
            fullWidth
            required
          />
          <SelectInput
            id="type-select"
            data={USER_TYPES.map((item) => ({
              label: item,
              value: item
            }))}
            value={state.type}
            label={t["User Type"]}
            helperText=""
            startAdornment={<ShieldOutlined sx={iconSx} />}
            onChange={({ target: { value } }) => {
              if (!value) return;
              const type = value as UserType;
              setState((prevState) => ({
                ...prevState,
                type,
                siteId: type === "super" ? "" : prevState.siteId
              }));
            }}
            fullWidth
          />
          {needsSite && (
            <SiteSelect
              value={state.siteId || undefined}
              startAdornment={<BusinessOutlined sx={iconSx} />}
              onChange={(siteId) => setState((prev) => ({ ...prev, siteId }))}
            />
          )}
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Add User"]}
          loading={isLoading}
          disabled={disabledSubmit}
        />
      </Box>
    </MyModal>
  );
}
